import { useContext, useEffect } from "react";

import { ParsedQuery } from "query-string";
import {
    createTheme,
    StyledEngineProvider,
    ThemeProvider
} from "@mui/material/styles";
import * as Sentry from "@sentry/react";

import AuthContext from "context/auth/authContext";
import LanguageContext from "context/language/languageContext";
import ParamsContext from "context/params/paramsContext";
import TutorialContext from "context/tutorial/tutorialContext";
import {
    FILTER_NAMES,
    NOT_FILTER_NAMES,
    PER_PAGE,
    ROUTES,
    useUrlQueryParams,
    useWindowLocation
} from "shared";

import ErrorBoundary, { ErrorBoundaryProps } from "../ErrorBoundary";
import { STEP_GROUP } from "../TutorialMode/types/enums";
import GlobalStyles from "./GlobalStyles";
import { useMuiStyles, useRoutes } from "./hooks";
import RouterContent from "./RouterContent";

const ErrorFallback = ({ error, resetError }: ErrorBoundaryProps) => {
    return <ErrorBoundary error={error} resetError={resetError} />;
};

const Routes = () => {
    const { filterDropdownOptions, setQueryParams, setResource } =
        useContext(ParamsContext);

    const { is2FaEnableFlowSeen, user, checkAuth } = useContext(AuthContext);

    const { language, languages } = useContext(LanguageContext);
    const { isInitialRender, setTutorialOpen } = useContext(TutorialContext);

    const { routes } = useRoutes();
    const { getUrlParams } = useUrlQueryParams();
    const { pathname, resource } = useWindowLocation();

    const currentLang =
        languages.find(({ id }) => id === language)?.muiLocale ??
        languages[0].muiLocale;

    const muiThemeStyles = useMuiStyles();

    const theme = createTheme(muiThemeStyles, currentLang);

    useEffect(() => {
        const isTutorialNotStarted = isInitialRender;

        const isCompanyOptionsLoaded =
            filterDropdownOptions[FILTER_NAMES.CompanyId] !== undefined;

        const showTutorial =
            is2FaEnableFlowSeen &&
            isTutorialNotStarted &&
            isCompanyOptionsLoaded;

        showTutorial && setTutorialOpen(true, 0, STEP_GROUP.FirstTimeUser);
        // eslint-disable-next-line
    }, [user, is2FaEnableFlowSeen, isInitialRender, filterDropdownOptions]);

    useEffect(() => {
        checkAuth();

        if (pathname !== ROUTES.Login) {
            const queryParamsFromUrl = getUrlParams();
            cleanUpQueryParams(queryParamsFromUrl);
        }

        // eslint-disable-next-line
    }, []);

    const cleanUpQueryParams = (queryParamsFromUrl: ParsedQuery<string>) => {
        Object.entries(queryParamsFromUrl).forEach(([id, value]) => {
            if (id === NOT_FILTER_NAMES.PerPage) {
                const correctValues: string[] = Object.values(PER_PAGE);

                if (
                    value === null ||
                    Array.isArray(value) ||
                    !correctValues.includes(value)
                ) {
                    queryParamsFromUrl[id] = "";
                }
            }

            if (value === null || id === FILTER_NAMES.RootCompany) {
                delete queryParamsFromUrl[id];
            }
        });

        const isResourceValid = routes
            .filter(({ isPrivate }) => isPrivate)
            .map(({ path }) => path)
            .includes(pathname as any);

        if (isResourceValid) {
            setQueryParams({
                params: {
                    ...queryParamsFromUrl
                }
            });

            setResource(resource);
        }
    };

    return (
        <>
            <GlobalStyles />

            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={theme}>
                    <Sentry.ErrorBoundary fallback={ErrorFallback}>
                        <RouterContent />
                    </Sentry.ErrorBoundary>
                </ThemeProvider>
            </StyledEngineProvider>
        </>
    );
};

export default Routes;
